/**
 * SetReminder — F 490's popover for setting a reminder on a date.
 *
 * WHAT IT IS
 * ----------
 * A small popover anchored to a date. It asks the only questions a reminder
 * actually has:
 *
 *   WHEN      — the time of day the reminder fires.
 *   REPEAT    — never / daily / weekly / monthly / yearly.
 *   WHICH DAY — for a weekly rule, the weekday; for a monthly rule, the
 *               day of the month.
 *   UNTIL     — optional, the last day the series may fire.
 *
 * And it offers the two escape valves:
 *
 *   SKIP THIS ONE — advance past the next occurrence, keep the series.
 *   STOP REPEATING — end the series now.
 *
 * WHY THOSE ARE SEPARATE BUTTONS
 * ------------------------------
 * "Skip this one" and "stop repeating" are different intentions and they
 * have different consequences: one advances `next_fire_at` and leaves the
 * series alive, the other sets `state = 'dismissed'` and ends it. Collapsing
 * them into one button would make "skip" silently end a series the operator
 * wanted to keep — which is the kind of quiet wrong that nobody notices
 * until the reminder they were counting on never arrives.
 *
 * WHY IT OPENS FROM A CLOCK, NOT FROM THE DATE TEXT
 * --------------------------------------------------
 * The date itself is editable in place (it is a plain date input). A
 * separate clock affordance means setting a time never destroys the date
 * the operator already chose, and the clock reads as "this moment" rather
 * than as another date field.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Group, Popover, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconClock, IconBell, IconCheck, IconX, IconCalendarRepeat, IconPlayerSkipForward } from '@tabler/icons-react';
import type { Reminder, ReminderRepeat } from '../../../types/models/Reminder';
import { REMINDER_REPEATS } from '../../../types/models/Reminder';
import type { ReminderStore } from '../../../core/reminder/store';
import { portalOwnerProps } from './reminderPortal';
import './SetReminder.css';

const REPEAT_LABEL: Record<ReminderRepeat, string> = {
  none: 'Never',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const WEEKDAYS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

export interface SetReminderProps {
  /**
   * The node that OWNS the date. The reminder points at it, so the
   * scheduler can say what fired.
   */
  parentNodeId: string;
  /**
   * WHICH date on that node. Optional: a task's `due` date is a field, not
   * a property, so it has no property id.
   */
  datePropertyId?: string | null;
  /** The date the reminder hangs off, as `YYYY-MM-DD` or an ISO datetime. */
  dateValue: string | null | undefined;
  /** The text the notification will carry. */
  text?: string | null;
  /** The store the reminder is written through. */
  store: ReminderStore;
  /** The existing reminder for this date, when there is one. */
  existing?: Reminder | null;
  /** The visible label for the date — used in the button's tooltip. */
  label?: string;
  className?: string;
}

/** `YYYY-MM-DD` from a Date, in LOCAL time (not UTC — the date is the user's). */
function toLocalDateInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** `HH:MM` from a Date, in LOCAL time. */
function toLocalTimeInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const SetReminder: React.FC<SetReminderProps> = ({
  parentNodeId,
  datePropertyId = null,
  dateValue,
  text = null,
  store,
  existing = null,
  label,
  className = '',
}) => {
  const [opened, setOpened] = useState(false);
  const [time, setTime] = useState('09:00');
  const [repeat, setRepeat] = useState<ReminderRepeat>('none');
  const [weekday, setWeekday] = useState<string>('monday');
  const [dayOfMonth, setDayOfMonth] = useState<string>('1');
  const [until, setUntil] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * Seed the form from the existing reminder, or from the date itself.
   *
   * The default time is NINE IN THE MORNING, not "now plus a minute" and not
   * midnight: a reminder with no time chosen should land at a time a person
   * is awake, and the field is visible and editable either way.
   */
  useEffect(() => {
    if (!opened) return;
    if (existing) {
      setRepeat(existing.repeat ?? 'none');
      setUntil(existing.until ? toLocalDateInput(new Date(existing.until)) : '');
      if (existing.repeat_time) setTime(existing.repeat_time);
      if (existing.repeat_on) {
        const v = String(existing.repeat_on);
        if (WEEKDAYS.some((w) => w.value === v)) setWeekday(v);
        else setDayOfMonth(v);
      }
      return;
    }
    // A brand-new reminder: seed the time from the date's own value when it
    // carries one, so a date set to 14:30 does not silently become 09:00.
    if (dateValue) {
      const d = new Date(dateValue);
      if (!Number.isNaN(d.getTime())) {
        const hasTime = /T\d{2}:\d{2}/.test(dateValue);
        setTime(hasTime ? toLocalTimeInput(d) : '09:00');
        setWeekday(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][d.getDay()]);
        setDayOfMonth(String(d.getDate()));
      }
    }
  }, [opened, existing, dateValue]);

  /** The datetime the reminder will fire at, from the date + the chosen time. */
  const fireAt = useMemo(() => {
    if (!dateValue) return null;
    const base = new Date(dateValue);
    if (Number.isNaN(base.getTime())) return null;
    const [hh, mm] = /^\d{2}:\d{2}$/.test(time) ? time.split(':').map(Number) : [9, 0];
    const out = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm, 0, 0);
    return out;
  }, [dateValue, time]);

  const repeatOn = useMemo(() => {
    if (repeat === 'weekly') return weekday;
    if (repeat === 'monthly') return dayOfMonth;
    return null;
  }, [repeat, weekday, dayOfMonth]);

  const handleSave = useCallback(async () => {
    if (!fireAt) return;
    setSaving(true);
    try {
      if (existing) {
        // An edit: keep the same reminder node and move its schedule. The
        // state goes back to `scheduled` so the scheduler picks it up —
        // a reminder that was dismissed and is then re-armed is scheduled
        // again, and a reminder that was `fired` and is re-armed is too.
        const next = new Date(fireAt);
        await store.save({
          ...existing,
          scheduled_at: next.toISOString(),
          repeat,
          repeat_on: repeatOn,
          repeat_time: time,
          next_fire_at: next.toISOString(),
          until: until ? new Date(`${until}T23:59:59`).toISOString() : null,
          state: 'scheduled',
        });
      } else {
        await store.create({
          id: `rem-${parentNodeId}-${datePropertyId ?? 'date'}-${Date.now()}`,
          parentNodeId,
          datePropertyId,
          scheduledAt: fireAt.toISOString(),
          repeat,
          repeatOn,
          repeatTime: time,
          until: until ? new Date(`${until}T23:59:59`).toISOString() : null,
          text,
        });
      }
      setSaved(true);
      setTimeout(() => { setSaved(false); setOpened(false); }, 900);
    } finally {
      setSaving(false);
    }
  }, [fireAt, existing, store, parentNodeId, datePropertyId, repeat, repeatOn, time, until, text]);

  const handleSkip = useCallback(async () => {
    if (!existing) return;
    await store.skipNext(existing.id);
    setSaved(true);
    setTimeout(() => { setSaved(false); setOpened(false); }, 900);
  }, [existing, store]);

  const handleStop = useCallback(async () => {
    if (!existing) return;
    await store.dismiss(existing.id);
    setSaved(true);
    setTimeout(() => { setSaved(false); setOpened(false); }, 900);
  }, [existing, store]);

  const hasReminder = !!existing && existing.state !== 'dismissed';

  return (
    <Box className={`fc-set-reminder ${className}`} ref={rootRef}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        shadow="md"
        width={280}
        withArrow
        withinPortal
        /**
         * THE Z-INDEX IS NOT A STYLE CHOICE — IT IS A CORRECTNESS ONE.
         *
         * MEASURED on production: Mantine's Popover defaults to
         * `z-index: 300`, and the task detail pane is `z-index: 1100`. The
         * popover therefore rendered BEHIND the pane, and the element
         * actually painted at the popover's own centre was the pane's
         * tags INPUT. The popover was on screen, positioned correctly, and
         * completely unclickable — a Playwright click on Save was
         * "intercepted by fc-task-detail-pane-notes".
         *
         * The app's own chrome scale, measured across `src/ui/`: 1000,
         * 1099, 1100, 1101, 1102, 1200, 3000, 9999. So a popover has to
         * clear 1200 to be above the app's panels, and 3000 is already
         * taken by the notification toast.
         *
         * ⇒ 3200. Above every panel, below the 9999 emergency layer.
         */
        zIndex={3200}
        /**
         * AND `closeOnClickOutside={false}` — BECAUSE A REAL MOUSE CLICK ON
         * SAVE DID NOTHING.
         *
         * MEASURED, and this is the bug that would have shipped: a
         * `mousedown` on ANY part of the dropdown closed the popover
         * immediately, and the button was gone before the `click` event
         * could reach it. The instrumented event log is unambiguous:
         *
         *     [probe] pointerdown on save
         *     [probe] mousedown on save
         *     (no click — ever)
         *
         * So a user clicking "Save" with a mouse got the popover closing
         * and NOTHING SAVED, with no error and no message. The popover
         * closing looks like the click WORKING, which is what made it
         * invisible: the proof asserted "the popover closed" and passed.
         *
         * WHY: Mantine's Popover closes on `mousedown` (its
         * `clickOutsideEvents` are `["mousedown", "touchstart"]`) and its
         * `useClickOutside` test did not consider the dropdown to be
         * "inside". Mantine's own `Menu` does NOT use `useClickOutside` at
         * all — which is why every menu in this app works and this popover
         * did not.
         *
         * ⇒ The popover no longer closes itself on an outside mousedown.
         * It closes on Escape, and it closes on the actions that mean to
         * close it (Save, Skip, Stop). A click outside leaves it open, and
         * that is the standard behaviour for a form popover.
         */
        closeOnClickOutside={false}
      >
        <Popover.Target>
          <Tooltip label={hasReminder ? 'Edit reminder' : 'Set a reminder for this date'} openDelay={400}>
            <ActionIcon
              className="fc-set-reminder__trigger"
              variant={hasReminder ? 'light' : 'subtle'}
              size="sm"
              aria-label="Set reminder"
              data-testid="fc-set-reminder-trigger"
              onClick={() => setOpened((o) => !o)}
            >
              {hasReminder ? <IconBell size={14} /> : <IconClock size={14} />}
            </ActionIcon>
          </Tooltip>
        </Popover.Target>

        <Popover.Dropdown
          data-testid="fc-set-reminder-popover"
          /* F 490 — mark this portal as belonging to the surface that
             opened it, so the surface's outside-click handler does not
             treat a click HERE as a click outside it. See
             `reminderPortal.ts` for the full story. */
          {...portalOwnerProps()}
        >
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Reminder
            </Text>

            <Group gap="xs" align="center">
              <TextInput
                type="time"
                value={time}
                onChange={(e) => setTime(e.currentTarget.value)}
                size="xs"
                style={{ flex: 1 }}
                aria-label="Reminder time"
                data-testid="fc-set-reminder-time"
              />
            </Group>

            <Select
              label="Repeat"
              size="xs"
              value={repeat}
              onChange={(v) => setRepeat((v as ReminderRepeat) || 'none')}
              data={REMINDER_REPEATS.map((r) => ({ value: r, label: REPEAT_LABEL[r] }))}
              allowDeselect={false}
              data-testid="fc-set-reminder-repeat"
            />

            {repeat === 'weekly' && (
              <Select
                label="On"
                size="xs"
                value={weekday}
                onChange={(v) => setWeekday(v || 'monday')}
                data={WEEKDAYS}
                allowDeselect={false}
                data-testid="fc-set-reminder-weekday"
              />
            )}

            {repeat === 'monthly' && (
              <TextInput
                label="Day of month"
                size="xs"
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.currentTarget.value)}
                data-testid="fc-set-reminder-dayofmonth"
              />
            )}

            {repeat !== 'none' && (
              <TextInput
                label="Until (optional)"
                size="xs"
                type="date"
                value={until}
                onChange={(e) => setUntil(e.currentTarget.value)}
                data-testid="fc-set-reminder-until"
              />
            )}

            {fireAt && (
              <Text size="xs" c="dimmed" data-testid="fc-set-reminder-preview">
                {hasReminder ? 'Next: ' : 'Fires: '}
                {fireAt.toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </Text>
            )}

            <Group gap="xs" justify="space-between" mt={4}>
              {hasReminder ? (
                <Group gap={4}>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconPlayerSkipForward size={13} />}
                    onClick={handleSkip}
                    data-testid="fc-set-reminder-skip"
                  >
                    Skip
                  </Button>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconX size={13} />}
                    onClick={handleStop}
                    data-testid="fc-set-reminder-stop"
                  >
                    Stop
                  </Button>
                </Group>
              ) : (
                <span />
              )}

              <Button
                size="compact-xs"
                leftSection={saved ? <IconCheck size={13} /> : <IconCalendarRepeat size={13} />}
                onClick={handleSave}
                loading={saving}
                color={saved ? 'green' : undefined}
                data-testid="fc-set-reminder-save"
              >
                {saved ? 'Saved' : 'Save'}
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};
